/**
 * Muestras API Service
 * Flujo operativo de toma de muestras: cola, atención, encuesta,
 * validación preanalítica, muestras por tubo, etiquetas y cierre.
 */

import { api } from "@/lib/api";
import type { TipoServicio } from "./ordenes";

// =============================================================================
// URLS
// =============================================================================

const ATENCIONES_URL = "/atenciones/";
const MUESTRAS_URL = "/muestras/";
const TIPOS_CONTENEDOR_URL = "/tipos-contenedor/";

// =============================================================================
// TYPES — Enums / Choices
// =============================================================================

export type EstadoAtencion = "ABIERTA" | "CERRADA" | "CERRADA_PARCIAL" | "CANCELADA";

export type EstadoMuestra = "PENDIENTE" | "TOMADA" | "INSUFICIENTE" | "RECHAZADA" | "REPETIDA";

export type ClasificacionAptitud =
  | "APTO"
  | "APTO_CON_OBSERVACION"
  | "NO_APTO"
  | "PENDIENTE_POR_TIEMPO";

export type TipoMuestraBiologica =
  | "suero"
  | "plasma"
  | "sangre_total_edta"
  | "sangre_total_heparinizada"
  | "orina"
  | "heces"
  | "secrecion"
  | "liquido"
  | "saliva";

export type MotivoRechazo =
  | "hemolizada"
  | "lipemica"
  | "volumen_insuficiente"
  | "mal_identificada"
  | "coagulada"
  | "otro";

export type TipoIncidencia = "ABANDONO" | "NO_PRESENTACION" | "SUSPENSION";

// Re-export from ordenes to avoid duplication
export type { TipoServicio } from "./ordenes";

// =============================================================================
// TYPES — TipoContenedor
// =============================================================================

export interface TipoContenedor {
  id: number;
  nombre: string;
  color: string;
  tipo_muestra: TipoMuestraBiologica;
  tipo_muestra_display: string;
  aditivo: string;
  volumen_ml: string;
  descripcion: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TipoContenedorCreateData {
  nombre: string;
  color: string;
  tipo_muestra: TipoMuestraBiologica;
  aditivo?: string;
  volumen_ml: string;
  descripcion?: string;
}

// =============================================================================
// TYPES — Cola de Atención
// =============================================================================

export interface ColaAtencionItem {
  id: number;
  numero: string;
  prioridad: string;
  prioridad_orden: number;
  tipo_servicio_derivado: TipoServicio;
  created_at: string;
  atencion_abierta_id: number | null;
  paciente_id: number;
  paciente_nombre: string;
  paciente_edad: string;
  paciente_sexo: string;
  examenes_pendientes: number;
}

// =============================================================================
// TYPES — Perfil Clínico Base
// =============================================================================

export interface PerfilClinicoBase {
  id: number;
  paciente: number;
  atencion: number;
  es_diabetico: boolean;
  es_hipertenso: boolean;
  enfermedad_renal: boolean;
  enfermedad_tiroidea: boolean;
  usa_anticoagulantes: boolean;
  tiene_anemia: boolean;
  plaquetas_bajas: boolean;
  sin_cambios: boolean;
  perfil_anterior: number | null;
  created_at: string;
  updated_at: string;
}

export interface PerfilClinicoBaseData {
  es_diabetico?: boolean;
  es_hipertenso?: boolean;
  enfermedad_renal?: boolean;
  enfermedad_tiroidea?: boolean;
  usa_anticoagulantes?: boolean;
  tiene_anemia?: boolean;
  plaquetas_bajas?: boolean;
}

// =============================================================================
// TYPES — Condición Actual
// =============================================================================

export interface CondicionActual {
  id: number;
  atencion: number;
  esta_en_ayunas: boolean;
  horas_ayuno: number | null;
  hora_ultima_comida: string | null;
  sintomas_actuales: string;
  medicamentos_recientes: string;
  motivo_examen: string;
  created_at: string;
  updated_at: string;
}

export interface CondicionActualData {
  esta_en_ayunas?: boolean;
  /** ISO 8601 datetime string — el backend calcula horas_ayuno automáticamente */
  hora_ultima_comida?: string | null;
  sintomas_actuales?: string;
  medicamentos_recientes?: string;
  motivo_examen?: string;
}

// =============================================================================
// TYPES — Validación Preanalítica
// =============================================================================

export interface ValidacionPreanalitica {
  id: number;
  atencion: number;
  orden_examen: number;
  examen_nombre: string;
  examen_codigo: string;
  dias_para_resultado: number | null;
  horas_para_resultado: number | null;
  clasificacion: ClasificacionAptitud;
  clasificacion_display: string;
  motivo_incumplimiento: string;
  observaciones: string;
  override_aplicado: boolean;
  override_por: number | null;
  override_por_nombre: string | null;
  override_justificacion: string;
  override_fecha: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// TYPES — Muestra
// =============================================================================

export interface MuestraExamen {
  id: number;
  orden_examen: number;
  examen_nombre: string;
  examen_codigo: string;
}

export interface RechazoMuestra {
  id: number;
  muestra: number;
  motivo: MotivoRechazo;
  motivo_display: string;
  descripcion: string;
  rechazado_por: number;
  rechazado_por_nombre: string;
  fecha_hora: string;
}

export interface Muestra {
  id: number;
  atencion: number;
  tipo_contenedor: number | null;
  tipo_contenedor_nombre: string | null;
  tipo_contenedor_color: string | null;
  estado: EstadoMuestra;
  estado_display: string;
  codigo_barras: string;
  volumen_requerido_ml: string | null;
  volumen_obtenido_ml: string | null;
  flebotomista: number | null;
  flebotomista_nombre: string | null;
  hora_toma: string | null;
  muestra_origen: number | null;
  observaciones: string;
  examenes: MuestraExamen[];
  rechazos: RechazoMuestra[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MuestraListItem {
  id: number;
  tipo_contenedor: number | null;
  tipo_contenedor_nombre: string | null;
  tipo_contenedor_color: string | null;
  estado: EstadoMuestra;
  estado_display: string;
  codigo_barras: string;
  volumen_requerido_ml: string | null;
  volumen_obtenido_ml: string | null;
  hora_toma: string | null;
  muestra_origen: number | null;
  areas: string[]; // Áreas de laboratorio de los exámenes (Issue #86)
}

// =============================================================================
// TYPES — Incidencia
// =============================================================================

export interface Incidencia {
  id: number;
  atencion: number;
  tipo: TipoIncidencia;
  tipo_display: string;
  descripcion: string;
  registrado_por: number;
  registrado_por_nombre: string;
  fecha_hora: string;
}

// =============================================================================
// TYPES — Atención
// =============================================================================

export interface Atencion {
  id: number;
  orden: number;
  orden_numero: string;
  flebotomista: number;
  flebotomista_nombre: string;
  estado: EstadoAtencion;
  estado_display: string;
  estacion: string;
  hora_inicio: string;
  hora_fin: string | null;
  verifico_nombre: boolean;
  verifico_fecha_nacimiento: boolean;
  verifico_identidad: boolean;
  verifico_numero_orden: boolean;
  identificadores_confirmados: number;
  identidad_verificada: boolean;
  tiempo_atencion: string | null;
  observaciones: string;
  // Datos del paciente (Issue #84)
  paciente_nombre: string;
  paciente_edad: string;
  paciente_sexo: string;
  paciente_documento: string;
  // Tipo de servicio de la orden (Issue #87)
  tipo_servicio: TipoServicio;
  tipo_servicio_display: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AtencionDetalle extends Atencion {
  perfil_clinico: PerfilClinicoBase[];
  condicion_actual: CondicionActual | null;
  validaciones: ValidacionPreanalitica[];
  muestras: MuestraListItem[];
  incidencias: Incidencia[];
}

// =============================================================================
// TYPES — Verificación de Completitud
// =============================================================================

export interface VerificacionCompletitud {
  completa: boolean;
  total_muestras: number;
  muestras_pendientes: number;
  muestras_definidas: number;
}

// =============================================================================
// TYPES — Input DTOs
// =============================================================================

export interface AbrirAtencionData {
  orden_id: number;
  verifico_nombre: boolean;
  verifico_fecha_nacimiento: boolean;
  verifico_identidad: boolean;
  verifico_numero_orden: boolean;
  estacion?: string;
}

export interface EncuestaData {
  perfil: PerfilClinicoBaseData;
  condicion: CondicionActualData;
  sin_cambios?: boolean;
}

export interface CerrarAtencionData {
  tipo_cierre: "TOTAL" | "PARCIAL" | "CANCELAR";
  justificacion?: string;
  incidencia?: {
    tipo: TipoIncidencia;
    descripcion?: string;
  } | null;
}

export interface RegistrarTomaData {
  volumen_obtenido_ml?: string | null;
}

export interface RechazarMuestraData {
  motivo: MotivoRechazo;
  descripcion?: string;
}

export interface ReimprimirEtiquetaData {
  motivo: string;
}

export interface OverrideManualData {
  justificacion: string;
}

// =============================================================================
// API — TipoContenedor
// =============================================================================

export async function getTiposContenedor(): Promise<{ count: number; results: TipoContenedor[] }> {
  const response = await api.get(TIPOS_CONTENEDOR_URL, { params: { page_size: 100 } });
  return response.data;
}

export async function createTipoContenedor(
  data: TipoContenedorCreateData
): Promise<TipoContenedor> {
  const response = await api.post(TIPOS_CONTENEDOR_URL, data);
  return response.data;
}

export async function updateTipoContenedor(
  id: number,
  data: Partial<TipoContenedorCreateData>
): Promise<TipoContenedor> {
  const response = await api.patch(`${TIPOS_CONTENEDOR_URL}${id}/`, data);
  return response.data;
}

export async function deleteTipoContenedor(id: number): Promise<void> {
  await api.delete(`${TIPOS_CONTENEDOR_URL}${id}/`);
}

export async function deactivateTipoContenedor(id: number): Promise<TipoContenedor> {
  const response = await api.post(`${TIPOS_CONTENEDOR_URL}${id}/deactivate/`);
  return response.data;
}

// =============================================================================
// API — Cola de Atención
// =============================================================================

export async function getColaAtencion(params: {
  sucursal: number;
  tipo_servicio?: TipoServicio;
}): Promise<ColaAtencionItem[]> {
  const response = await api.get(`${ATENCIONES_URL}cola/`, { params });
  return response.data;
}

// =============================================================================
// API — Atención
// =============================================================================

export async function getAtencionDetalle(id: number): Promise<AtencionDetalle> {
  const response = await api.get(`${ATENCIONES_URL}${id}/`);
  return response.data;
}

export async function abrirAtencion(data: AbrirAtencionData): Promise<Atencion> {
  const response = await api.post(`${ATENCIONES_URL}abrir/`, data);
  return response.data;
}

export async function registrarEncuesta(
  atencionId: number,
  data: EncuestaData
): Promise<{ perfil: PerfilClinicoBase; condicion: { id: number; atencion: number } }> {
  const response = await api.post(`${ATENCIONES_URL}${atencionId}/encuesta/`, data);
  return response.data;
}

export async function getPerfilClinicoPrevio(
  atencionId: number
): Promise<PerfilClinicoBase | null> {
  const response = await api.get(`${ATENCIONES_URL}${atencionId}/perfil-clinico/`);
  return response.data;
}

export async function validarExamenes(atencionId: number): Promise<ValidacionPreanalitica[]> {
  const response = await api.post(`${ATENCIONES_URL}${atencionId}/validar/`, {});
  return response.data;
}

export async function overrideManual(
  atencionId: number,
  ordenExamenId: number,
  data: OverrideManualData
): Promise<ValidacionPreanalitica> {
  const response = await api.post(
    `${ATENCIONES_URL}${atencionId}/override/${ordenExamenId}/`,
    data
  );
  return response.data;
}

export async function generarMuestras(atencionId: number): Promise<MuestraListItem[]> {
  const response = await api.post(`${ATENCIONES_URL}${atencionId}/generar-muestras/`, {});
  return response.data;
}

export async function verificarCompletitud(atencionId: number): Promise<VerificacionCompletitud> {
  const response = await api.get(`${ATENCIONES_URL}${atencionId}/verificar-completitud/`);
  return response.data;
}

export async function cerrarAtencion(
  atencionId: number,
  data: CerrarAtencionData
): Promise<Atencion> {
  const response = await api.post(`${ATENCIONES_URL}${atencionId}/cerrar/`, data);
  return response.data;
}

export async function getEtiquetasAtencion(atencionId: number): Promise<Blob> {
  const response = await api.get(`${ATENCIONES_URL}${atencionId}/etiquetas/`, {
    responseType: "blob",
  });
  return response.data;
}

// =============================================================================
// API — Muestras
// =============================================================================

export async function getMuestra(id: number): Promise<Muestra> {
  const response = await api.get(`${MUESTRAS_URL}${id}/`);
  return response.data;
}

export async function registrarToma(muestraId: number, data: RegistrarTomaData): Promise<Muestra> {
  const response = await api.post(`${MUESTRAS_URL}${muestraId}/registrar-toma/`, data);
  return response.data;
}

export async function rechazarMuestra(
  muestraId: number,
  data: RechazarMuestraData
): Promise<Muestra> {
  const response = await api.post(`${MUESTRAS_URL}${muestraId}/rechazar/`, data);
  return response.data;
}

export async function marcarInsuficiente(muestraId: number): Promise<Muestra> {
  const response = await api.post(`${MUESTRAS_URL}${muestraId}/insuficiente/`, {});
  return response.data;
}

export async function repetirMuestra(muestraId: number): Promise<Muestra> {
  const response = await api.post(`${MUESTRAS_URL}${muestraId}/repetir/`, {});
  return response.data;
}

export async function getEtiqueta(muestraId: number): Promise<Blob> {
  const response = await api.get(`${MUESTRAS_URL}${muestraId}/etiqueta/`, {
    responseType: "blob",
  });
  return response.data;
}

export async function reimprimirEtiqueta(
  muestraId: number,
  data: ReimprimirEtiquetaData
): Promise<Blob> {
  const response = await api.post(`${MUESTRAS_URL}${muestraId}/reimprimir-etiqueta/`, data, {
    responseType: "blob",
  });
  return response.data;
}

// =============================================================================
// TYPES — Estadísticas (M4.10)
// =============================================================================

export type PeriodoEstadisticas = "hoy" | "semana" | "mes" | "personalizado";

export interface EstadisticasResumen {
  total: number;
  cerradas: number;
  canceladas: number;
  abiertas: number;
  tiempo_atencion_promedio_min: number | null;
  tiempo_cola_promedio_min: number | null;
}

export interface EstadisticasFlebotomista {
  flebotomista_id: number;
  flebotomista_nombre: string;
  total: number;
  cerradas: number;
  canceladas: number;
}

export interface EstadisticasHora {
  hora: number;
  total: number;
}

export interface EstadisticasMuestras {
  fecha_inicio: string;
  fecha_fin: string;
  resumen: EstadisticasResumen;
  por_estado: Record<string, number>;
  por_flebotomista: EstadisticasFlebotomista[];
  por_hora: EstadisticasHora[];
}

export interface EstadisticasParams {
  sucursal?: number;
  periodo?: PeriodoEstadisticas;
  fecha_inicio?: string;
  fecha_fin?: string;
}

// =============================================================================
// API — Estadísticas (M4.10)
// =============================================================================

export async function getEstadisticasMuestras(
  params: EstadisticasParams
): Promise<EstadisticasMuestras> {
  const response = await api.get(`${ATENCIONES_URL}estadisticas/`, { params });
  return response.data;
}
